import { useActionState, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlutoClientException } from '@/PlutoClientException';
import { State } from '@/types';
import { changePassword } from '@/tmp/UserService';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { createInitState } from '@/utilities/formUtils';
import { useNotificationStore } from '@/stores/notificationstore';
import { ChangePasswordForm } from './ChangePasswordForm';

export type ChangePasswordFields = {
  password: string;
  passwordAgain: string;
};

export default function ChangePasswordPage({
  successfulChange,
  user,
  hash,
}: {
  successfulChange: () => void;
  user: string;
  hash: string;
}) {
  const resetPasswordState = createInitState<ChangePasswordFields>(['password', 'passwordAgain']);
  const [state, formAction] = useActionState(tryResetPassword, resetPasswordState);
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen);
  const showNotification = useNotificationStore((state) => state.showNotification);

  const { t } = useTranslation();

  useEffect(() => {
    function handleResize() {
      setPhone(isPhoneOrHighScreen);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  async function tryResetPassword(
    prev: State<ChangePasswordFields>,
    formPayload: ChangePasswordFields
  ): Promise<State<ChangePasswordFields>> {
    const missingPassword = !formPayload.password;
    const missingPasswordAgain = !formPayload.passwordAgain;

    if (missingPassword || missingPasswordAgain) {
      return {
        ...prev,
        errors: {
          password: missingPassword ? t('login.error.missingPassword') : undefined,
          passwordAgain: missingPasswordAgain ? t('login.error.missingPassword') : undefined,
        },
        globalError: undefined,
        success: false,
      };
    }

    if (
      formPayload.password !== formPayload.passwordAgain &&
      (formPayload.password.length && formPayload.passwordAgain.length) > 0
    ) {
      showNotification('error', t('login.resetPassword.passwordDontMatch'), undefined, 5);
      return {
        ...prev,
        errors: {
          password: undefined,
          passwordAgain: undefined,
        },
        globalError: undefined,
        success: false,
      };
    }

    try {
      await changePassword(user, hash, formPayload.password);
      showNotification('success', t('login.resetPassword.succesfulChange'));
      successfulChange();
      return {
        globalError: undefined,
        errors: { password: undefined, passwordAgain: undefined },
        success: true,
      };
    } catch (error) {
      if (!(error instanceof PlutoClientException)) {
        showNotification('error', t('login.resetPassword.passwordFormatError'));
        return {
          globalError: t('login.resetPassword.passwordFormatError'),
          errors: { password: undefined, passwordAgain: undefined },
        };
      }
      return resetPasswordState;
    }
  }

  return <ChangePasswordForm state={state} formAction={formAction} isPhone={isPhone} />;
}
